import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { FileText, Calendar, MessageSquare, Download, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useFormProtection } from '../hooks/useFormProtection';
import HoneypotField from './HoneypotField';
import RecaptchaNotice from './RecaptchaNotice';

const PDFThankYouPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [contactFormData, setContactFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: "I'd like to schedule a 30-minute workflow audit call to review my Practice CS setup.",
    consent: false
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const { honeypotProps, onInteraction, getProtectionPayload } = useFormProtection('contact_form');

  useEffect(() => {
    const validateTokenData = async () => {
      if (!token) {
        setError('Invalid access token');
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/pdf-thank-you/${token}`);
        
        if (response.data.status === 'success') {
          setTokenData(response.data.data);
          // Pre-fill contact form with user data
          const firstName = response.data.data.full_name ? response.data.data.full_name.split(' ')[0] : '';
          const lastName = response.data.data.full_name ? response.data.data.full_name.split(' ').slice(1).join(' ') : '';
          setContactFormData(prev => ({
            ...prev,
            first_name: firstName,
            last_name: lastName,
            email: response.data.data.email
          }));
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Invalid or expired link');
      } finally {
        setIsLoading(false);
      }
    };

    validateTokenData();
  }, [token]);

  const handleDownload = () => {
    // Create a temporary link element for direct download
    const link = document.createElement('a');
    link.href = `/api/pdf-download/${token}`;
    link.download = 'Practice-Dashboard-Executive-Sample.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleContactInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    setContactMessage('');

    try {
      const protection = await getProtectionPayload();
      const response = await axios.post('/api/contact/full', { ...contactFormData, ...protection });

      if (response.data.status === 'success') {
        setContactMessage("Thank you! We'll get back to you soon to schedule your workflow audit.");
        setContactFormData(prev => ({
          ...prev,
          message: "I'd like to schedule a 30-minute workflow audit call to review my Practice CS setup.",
          consent: false
        }));
      } else {
        setContactMessage('An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setContactMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const getTimeRemaining = () => {
    if (!tokenData?.expires_at) return '';
    
    const now = new Date();
    const expires = new Date(tokenData.expires_at);
    const diff = expires - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Validating access...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Access Error
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              {error}
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Return to Home
            </button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8 sm:mb-12">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Your Executive Dashboard Sample is Ready
            </h1>
            <p className="text-lg sm:text-xl text-gray-600">
              Thanks for your interest, {tokenData?.full_name?.split(' ')[0] || 'there'}! 
              Your PDF has been emailed to you and is ready for download.
            </p>
          </div>

          {/* Download Section */}
          <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-2xl p-6 sm:p-8 mb-8 sm:mb-12 text-center border border-gray-200">
            <FileText className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              📊 Executive Dashboard Sample
            </h2>
            <p className="text-gray-600 mb-6">
              See what your Practice CS data visualization could look like
            </p>
            
            <button
              onClick={handleDownload}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 min-h-[48px] flex items-center justify-center mx-auto mb-4"
            >
              <Download className="w-5 h-5 mr-2" />
              Download PDF Sample
            </button>

            <div className="flex items-center justify-center text-sm text-gray-500">
              <Clock className="w-4 h-4 mr-1" />
              {getTimeRemaining()}
            </div>
          </div>

          {/* Secondary CTAs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Workflow Audit CTA */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="text-center mb-6">
                <MessageSquare className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Request a Free 30-Minute Workflow Audit
                </h3>
                <p className="text-gray-600">
                  Let's review your Practice CS setup and identify optimization opportunities
                </p>
              </div>

              <form onSubmit={handleContactSubmit} onFocus={onInteraction} className="space-y-4">
                <HoneypotField {...honeypotProps} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="first_name"
                    value={contactFormData.first_name}
                    onChange={handleContactInputChange}
                    required
                    placeholder="First Name"
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[50px]"
                  />
                  <input
                    type="text"
                    name="last_name"
                    value={contactFormData.last_name}
                    onChange={handleContactInputChange}
                    required
                    placeholder="Last Name"
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[50px]"
                  />
                </div>

                <input
                  type="email"
                  name="email"
                  value={contactFormData.email}
                  onChange={handleContactInputChange}
                  required
                  placeholder="Email Address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[50px]"
                />

                <input
                  type="tel"
                  name="phone"
                  value={contactFormData.phone}
                  onChange={handleContactInputChange}
                  placeholder="Phone Number (Optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[50px]"
                />

                <textarea
                  name="message"
                  value={contactFormData.message}
                  onChange={handleContactInputChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                  placeholder="Tell us about your Practice CS setup..."
                />

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="consent"
                    name="consent"
                    checked={contactFormData.consent}
                    onChange={handleContactInputChange}
                    required
                    className="mt-1 mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="consent" className="text-sm text-gray-600">
                    I agree to be contacted about Practice Dashboard services and workflow audit scheduling.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmittingContact}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 min-h-[48px] flex items-center justify-center"
                >
                  {isSubmittingContact ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    'Request Workflow Audit'
                  )}
                </button>

                {contactMessage && (
                  <div className={`mt-4 p-3 rounded-lg text-center ${
                    contactMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {contactMessage}
                  </div>
                )}

                <RecaptchaNotice />
              </form>
            </div>

            {/* Calendly CTA */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="text-center mb-6">
                <Calendar className="w-10 h-10 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Book a Personalized Walkthrough
                </h3>
                <p className="text-gray-600 mb-6">
                  Schedule a live demo to see how Practice Dashboard can transform your data visualization
                </p>
              </div>

              <div className="text-center">
                <a
                  href="https://calendly.com/pavlo-martinovych/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 min-h-[48px]"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule a Call
                </a>
                <p className="text-sm text-gray-500 mt-4">
                  30-minute personalized demo • No commitment required
                </p>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">What we'll cover:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Your specific Practice CS setup</li>
                  <li>• Custom dashboard possibilities</li>
                  <li>• Integration requirements</li>
                  <li>• Implementation timeline</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer Message */}
          <div className="text-center mt-8 sm:mt-12">
            <p className="text-gray-600 mb-2">Questions? Feel free to reach out!</p>
            <p className="font-semibold text-gray-900">
              Pavlo Martinovych | Founder, Practice Dashboard
            </p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PDFThankYouPage;